import React from 'react';
import { styled } from '@stitches/react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const RewardSystemSection: React.FC = () => {
  const { t } = useTranslation(); // 使用国际化钩子

  return (
    <SectionContainer>
      <ContentContainer>
        <HeaderSection>
          <SectionTitle>{t('home.rewards.title')}</SectionTitle>
          <SectionSubtitle>
            {t('home.rewards.subtitle')}
          </SectionSubtitle>
        </HeaderSection>

        <StepsContainer>
          {rewardSteps(t).map((step, index) => (
            <StepCard
              key={step.id}
              initial="offscreen"
              whileInView="onscreen"
              viewport={{ once: true, amount: 0.2 }}
              variants={cardVariants}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <StepHeader>
                <StepNumber>{step.step}</StepNumber>
                <StepTitle>{step.title}</StepTitle>
              </StepHeader>

              <StepContent>
                <StepMainContent>
                  <StepDescription>
                    {step.description}
                  </StepDescription>

                  {step.highlights && (
                    <HighlightsList>
                      {step.highlights.map((highlight, idx) => (
                        <HighlightItem key={idx}>
                          <HighlightIcon>✓</HighlightIcon>
                          <HighlightText>{highlight}</HighlightText>
                        </HighlightItem>
                      ))}
                    </HighlightsList>
                  )}
                </StepMainContent>
              </StepContent>

              <StepConnector
                show={index < rewardSteps.length - 1}
              />
            </StepCard>
          ))}
        </StepsContainer>

        <CTASection>
          <CTAButton
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.8 }}
            onClick={() => {
              window.open('http://nft.bitengine.org/shop', '_blank', 'noopener,noreferrer');
            }}
          >
            {t('home.rewards.becomeNode')}
          </CTAButton>
        </CTASection>
      </ContentContainer>
    </SectionContainer>
  );
};

// Section container
const SectionContainer = styled('section', {
  width: '100%',
  backgroundColor: 'var(--surface-color)',
  padding: 'var(--spacing-xxl) 0',
  position: 'relative',
  overflow: 'hidden',
  backgroundImage: 'url(/images/bg123.jpg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundAttachment: 'fixed',

  // 深色蒙版确保文本可读性
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0.6) 100%)',
    pointerEvents: 'none',
    zIndex: 0,
  },

  // 添加额外的蓝色渐变层以保持品牌色调
  '&::after': {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'radial-gradient(circle at 50% 50%, rgba(0, 114, 255, 0.15) 0%, transparent 70%)',
    pointerEvents: 'none',
    zIndex: 1,
  },

  '@media (max-width: 768px)': {
    backgroundAttachment: 'scroll', // 移动端避免fixed可能导致的性能问题
  }
});

// Content container
const ContentContainer = styled('div', {
  maxWidth: 'var(--container-width)',
  margin: '0 auto',
  padding: '0 var(--spacing-lg)',
  position: 'relative',
  zIndex: 1
});

// Header section
const HeaderSection = styled('div', {
  textAlign: 'center',
  marginBottom: 'var(--spacing-xxl)',
});

// Section title
const SectionTitle = styled('h2', {
  fontSize: 'var(--font-size-display)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--text-primary)',
  marginBottom: 'var(--spacing-md)',
  background: 'linear-gradient(135deg, var(--button-primary) 0%, #FFD700 50%, #FFED4E 100%)',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',

  '@media (max-width: 768px)': {
    fontSize: 'var(--font-size-xxl)',
  }
});

// Section subtitle
const SectionSubtitle = styled('p', {
  fontSize: 'var(--font-size-lg)',
  color: 'var(--text-secondary)',
  lineHeight: 1.6,
  maxWidth: '800px',
  margin: '0 auto',

  '@media (max-width: 768px)': {
    fontSize: 'var(--font-size-md)',
  }
});

// Steps container
const StepsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-lg)',
  marginBottom: 'var(--spacing-xxl)',

  '@media (min-width: 1024px)': {
    flexDirection: 'row',
    alignItems: 'stretch', // 改为stretch确保等高
    gap: 'var(--spacing-xl)',
  }
});

// Step card
const StepCard = styled(motion.div, {
  backgroundColor: 'var(--primary-dark)', // 项目深色背景
  borderRadius: 'var(--border-radius-lg)',
  padding: 'var(--spacing-xl)',
  border: '1px solid rgba(255, 184, 0, 0.3)',
  position: 'relative',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5), 0 4px 16px rgba(255, 184, 0, 0.1)',
  backdropFilter: 'blur(15px) saturate(180%)',
  WebkitBackdropFilter: 'blur(15px) saturate(180%)', // Safari支持
  display: 'flex',
  flexDirection: 'column',

  // 添加内部光晕效果
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    bottom: '0',
    background: 'linear-gradient(135deg, rgba(255, 184, 0, 0.1) 0%, transparent 50%, rgba(255, 215, 0, 0.05) 100%)',
    borderRadius: 'var(--border-radius-lg)',
    pointerEvents: 'none',
    zIndex: -1,
  },

  '@media (max-width: 1023px)': {
    minHeight: 'auto', // 移动端自适应高度
    backgroundColor: 'var(--primary-dark)', // 移动端背景
  },

  '@media (min-width: 1024px)': {
    flex: '1 1 calc(33.333% - var(--spacing-xl))', // 等宽分布
    maxWidth: 'calc(33.333% - var(--spacing-xl))',
    minHeight: '480px', // 桌面端固定最小高度确保一致性
  },

  '&:hover': {
    borderColor: 'rgba(255, 184, 0, 0.6)',
    backgroundColor: 'rgba(8, 12, 20, 0.95)', // 悬停时稍微变亮的深色背景
    boxShadow: '0 20px 40px rgba(255, 184, 0, 0.3)',
    transform: 'translateY(-8px) scale(1.02)',
    transition: 'all var(--transition-normal) var(--ease-out)',

    '&::before': {
      background: 'linear-gradient(135deg, rgba(255, 184, 0, 0.15) 0%, transparent 50%, rgba(255, 215, 0, 0.1) 100%)',
    }
  }
});

// Step header
const StepHeader = styled('div', {
  marginBottom: 'var(--spacing-lg)',
});

// Step number
const StepNumber = styled('div', {
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, var(--button-primary) 0%, #FFD700 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--button-primary-text)',
  marginBottom: 'var(--spacing-md)',
  boxShadow: '0 4px 12px rgba(255, 184, 0, 0.4)',
  border: '2px solid rgba(255, 184, 0, 0.3)',
});

// Step title
const StepTitle = styled('h3', {
  fontSize: 'var(--font-size-xl)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--text-primary)',
  marginBottom: 'var(--spacing-sm)',
});

// Step content
const StepContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  flex: '1', // 让内容区域占据剩余空间
  justifyContent: 'space-between', // 主要内容和奖励区域分别在顶部和底部
});

// Step main content
const StepMainContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-md)',
  flex: '1',
});



// Step description
const StepDescription = styled('p', {
  fontSize: 'var(--font-size-md)',
  color: 'var(--text-secondary)',
  lineHeight: 1.6,
});

// Highlights list
const HighlightsList = styled('ul', {
  listStyle: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-sm)',
});

// Highlight item
const HighlightItem = styled('li', {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-sm)',
});

// Highlight icon
const HighlightIcon = styled('span', {
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, var(--button-primary) 0%, #FFD700 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '12px',
  color: 'var(--button-primary-text)',
  fontWeight: 'var(--font-weight-bold)',
  boxShadow: '0 2px 6px rgba(255, 184, 0, 0.3)',
});

// Highlight text
const HighlightText = styled('span', {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--text-primary)',
});



// Step connector
const StepConnector = styled('div', {
  variants: {
    show: {
      true: {
        display: 'block',
      },
      false: {
        display: 'none',
      }
    }
  },

  position: 'absolute',
  right: `calc(-1 * var(--spacing-xl) / 2)`, // 更精确的定位
  top: '50%',
  transform: 'translateY(-50%)',
  width: 'var(--spacing-xl)',
  height: '2px',
  background: 'linear-gradient(90deg, var(--button-primary) 0%, #FFD700 50%, transparent 100%)',
  zIndex: 10,
  backdropFilter: 'blur(2px)',

  '&::after': {
    content: '""',
    position: 'absolute',
    right: '0',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '8px',
    height: '8px',
    backgroundColor: 'var(--button-primary)',
    borderRadius: '50%',
    boxShadow: '0 0 12px rgba(255, 184, 0, 0.6)',
  },

  '@media (max-width: 1023px)': {
    display: 'none !important',
  }
});

// CTA section
const CTASection = styled('div', {
  textAlign: 'center',
});

// CTA button
const CTAButton = styled(motion.button, {
  backgroundColor: 'var(--button-primary)',
  color: '#000000',
  border: 'none',
  borderRadius: 'var(--border-radius-md)',
  padding: 'var(--spacing-md) var(--spacing-xxl)',
  fontSize: 'var(--font-size-lg)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  boxShadow: '0 4px 20px rgba(255, 184, 0, 0.3)',

  '&:hover': {
    backgroundColor: '#FFD700',
    boxShadow: '0 6px 30px rgba(255, 184, 0, 0.4)',
    transform: 'translateY(-2px)',
    transition: 'all var(--transition-normal) var(--ease-out)',
  }
});

// Reward steps data - 使用国际化翻译函数
const rewardSteps = (t: any) => [
  {
    id: 1,
    step: "1",
    title: t('home.rewards.step1.title'),
    description: t('home.rewards.step1.description'),
    highlights: [
      t('home.rewards.step1.highlight1'),
      t('home.rewards.step1.highlight2'),
      t('home.rewards.step1.highlight3')
    ]
  },
  {
    id: 2,
    step: "2",
    title: t('home.rewards.step2.title'),
    description: t('home.rewards.step2.description'),
    highlights: [
      t('home.rewards.step2.highlight1'),
      t('home.rewards.step2.highlight2')
    ]
  },
  {
    id: 3,
    step: "3",
    title: t('home.rewards.step3.title'),
    description: t('home.rewards.step3.description'),
    highlights: [
      t('home.rewards.step3.highlight1'),
      t('home.rewards.step3.highlight2'),
      t('home.rewards.step3.highlight3')
    ]
  }
];

// Card animation variants
const cardVariants = {
  offscreen: {
    y: 50,
    opacity: 0
  },
  onscreen: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      bounce: 0.4,
      duration: 0.8
    }
  }
};

export default RewardSystemSection; 